import { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HandleChange from '../../../../Helpers/HandleChange';
import {
    MANAGE_CONTRACTORS_MODEL,
} from '../SubComponents/ManageContractors/Model/Model';
import {
    GetContractorRestriction, GetSupplierRestriction,
    UpdateContractorRestriction, UpdateSupplierRestriction
} from '../../../../reduxToolkit/restrictions/RestrictionApi';
import { useDispatch } from 'react-redux';
import { errorMsg, getDateLong } from '../../../../Apis/functions';
import { toast } from 'react-toastify';
import { FREQUENCY_RENEWAL } from '../../../../enums/FrequencyRenewalEnum';
import { PERIOD_TIME_UNIT } from '../../../../enums/PeriodTimeUnitEnum';
import { USER_TYPE_MODEL } from '../../../../enums/userTypeEnum';

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */


function ManageContractorsApp({
    ref,
    userType
}) {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const [config, setConfig] = useState({ ...MANAGE_CONTRACTORS_MODEL });
    const [emptyFields, setEmptyFields] = useState(false);
    const [modified, setModified] = useState(false);
    const { handleChange: onChange } = HandleChange({ model: config, setModel: setConfig });
    const [txtLadDays, setTxtLadDays] = useState("");
    const [txtFrequencyValue, setTxtFrequencyValue] = useState("");
    const [emptyFieldsCustom, setEmptyFieldsCustom] = useState(false);
    const [isValidFrequency, setIsValidFrequency] = useState(true);
    const [isValidLeadDays, setIsValidLeadDays] = useState(true);

    const TIME_UNIT_OPTIONS_TRANS = PERIOD_TIME_UNIT.map(el => ({
        ...el,
        name: t(el.name)
    }));

    /**
     * hanle to click update data
     */
    useImperativeHandle(ref, () => ({
        handleUpdate
    }));

    /**
     * handle to get data
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * handle to get data where modified has changes
     */
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Handle to validate when value equal zero,
     */
    useEffect(() => {
        const isCustomInterval = config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL;
        const frequencyValue = Number(config?.frequencyValue);
        const leadDays = Number(config?.leadDays);

        // -------- FREQUENCY --------
        if (isCustomInterval) {
            if (frequencyValue <= 0) {
                setIsValidFrequency(false);
                setTxtFrequencyValue(t("the_frequency_value_cannot_be_zero"));
            } else if (!isValidFrequencyAndLeadDays()) {
                setIsValidFrequency(false);
                setTxtFrequencyValue(t("it_must_be_greater_than_the_allowed_lead_time"));
            } else {
                setIsValidFrequency(true);
                setTxtFrequencyValue("");
            }
        }else{
            setIsValidFrequency(true);
            setTxtFrequencyValue("");
        }

        // -------- LEAD DAYS --------
        if (leadDays <= 0) {
            setIsValidLeadDays(false);
            setTxtLadDays(t("the_lead_days_cannot_be_zero"));
        } else if (isCustomInterval && !isValidFrequencyAndLeadDays()) {
            setIsValidLeadDays(false);
            setTxtLadDays(t("it_must_be_less_than_the_frequency"));
        } else {
            setIsValidLeadDays(true);
            setTxtLadDays("");
        }

    }, [config?.renewal, config.frequencyValue, config.leadDays]);

    const isValidFrequencyAndLeadDays = () => {
        if(Number(config.frequencyValue) >= Number(config.leadDays)){
            return true;
        }
        return false;
    }

    /**
     * handle to get data for contractor restriction
     */
    const getData = () => {
        dispatch(userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
            GetContractorRestriction() : GetSupplierRestriction()
        ).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                const start = data?.startDatePeriod ? data?.startDatePeriod : new Date();
                const end = data?.endDatePeriod ? data?.endDatePeriod : new Date();
                setConfig({ ...MANAGE_CONTRACTORS_MODEL, ...data, start: start, end: end });
            } else errorMsg(code);
        });
    }

    /**
     * handle to control change
     * @param evt
     */
    const handleOnChange = (evt) => {
        const value = evt.target.value;
        const name = evt.target.name;
        if (name === "renewal") {
            setConfig((prevState) => (
                {
                    ...prevState, [name]: value,
                    start: new Date(),
                    end: new Date(),
                }
            ));
            if (value === FREQUENCY_RENEWAL.ONE_TIME_ONLY) {
                setConfig((prevState) => (
                    {
                        ...prevState, [name]: value,
                        frequencyValue: 0
                    }
                ));
            }
        } else onChange(evt)
    }

    /**
     * handle to update contractor restriction
     */
    const handleUpdate = () => {
        const { renewal, waitTime } = config;
        const valueWaitTime = parseInt(waitTime);

        if (isNaN(valueWaitTime) || !isValidLeadDays) {
            setEmptyFields(true);
            return;
        }

        if (renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL && (!config.isValidCustom() || !isValidFrequency || !isValidLeadDays)) {
            setEmptyFieldsCustom(true);
            return;
        }

        const body = {
            id: config.id,
            waitTime: config.waitTime,
            renewal: config.renewal,
            frequencyValue: renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY ? 0 : config.frequencyValue,
            timeUnit: config.timeUnit,
            startDatePeriod: getDateLong(config?.start),
            endDatePeriod: config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY ? null : getDateLong(config?.end),
            leadDays: config.leadDays,
        };
        dispatch(userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
            UpdateContractorRestriction(body) : UpdateSupplierRestriction(body)
        ).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                setModified(true);
                toast.success(t('has_been_updated_successfully'));
            }
            else errorMsg(code)
        });
    }

    return {
        config,
        onChange: (e) => handleOnChange(e),
        TIME_UNIT_OPTIONS_TRANS,
        setConfig,
        handleUpdate,
        emptyFields,
        txtLadDays,
        txtFrequencyValue,
        emptyFieldsCustom,
        isValidFrequency,
        setIsValidFrequency,
        isValidLeadDays,
        setIsValidLeadDays
    };
}

export default ManageContractorsApp;
